import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AlarmClock, X } from 'lucide-react';
import { toLocalInput } from '../format';

// Typing a date into the picker for "call her back after lunch" was four clicks
// and a calculation. These are the answers people actually give, and the
// reminders go quiet until the moment picked here.
function inHours(hours) {
  const when = new Date();
  when.setHours(when.getHours() + hours, when.getMinutes(), 0, 0);
  return when;
}

function atRemindTime(when) {
  when.setHours(9, 30, 0, 0);
  return when;
}

function inDays(days) {
  const when = new Date();
  when.setDate(when.getDate() + days);
  return atRemindTime(when);
}

// Clamped to the month's last day, so a month from 31 January is not 3 March.
function inMonths(months) {
  const when = new Date();
  const day = when.getDate();
  when.setDate(1);
  when.setMonth(when.getMonth() + months);
  when.setDate(Math.min(day, new Date(when.getFullYear(), when.getMonth() + 1, 0).getDate()));
  return atRemindTime(when);
}

const SNOOZE_OPTIONS = [
  { key: 'twoHours', at: () => inHours(2) },
  { key: 'tomorrow', at: () => inDays(1) },
  { key: 'threeDays', at: () => inDays(3) },
];

const COME_BACK_OPTIONS = [
  { key: 'oneWeek', at: () => inDays(7) },
  { key: 'twoWeeks', at: () => inDays(14) },
  { key: 'oneMonth', at: () => inMonths(1) },
  { key: 'threeMonths', at: () => inMonths(3) },
];

// Saved by its button, not on change: typing a date changes it digit by digit.
function ComeBackDate({ onPick }) {
  const { t } = useTranslation('common');
  const [day, setDay] = useState('');
  const earliest = toLocalInput(inDays(1).toISOString()).slice(0, 10);

  const submit = (event) => {
    event.preventDefault();
    const [year, month, date] = day.split('-').map(Number);
    onPick(atRemindTime(new Date(year, month - 1, date)));
  };

  return (
    <form className="crm-snooze-date" onSubmit={submit}>
      <span className="crm-field-label">{t('crm.lead.snooze.orDate')}</span>
      <div className="crm-snooze-date-row">
        <input
          className="crm-input"
          type="date"
          aria-label={t('crm.lead.snooze.orDate')}
          min={earliest}
          value={day}
          onClick={(event) => event.currentTarget.showPicker()}
          onChange={(event) => setDay(event.target.value)}
        />
        <button type="submit" className="crm-snooze-date-save" disabled={day < earliest}>
          {t('crm.lead.snooze.remindOnDate')}
        </button>
      </div>
    </form>
  );
}

export default function SnoozePicker({ value, onPick, comeBack, withCalendar }) {
  const { t } = useTranslation('common');
  const options = comeBack ? COME_BACK_OPTIONS : SNOOZE_OPTIONS;

  return (
    <div className="crm-snooze">
      <span className="crm-field-label">
        <AlarmClock size={13} /> {t('crm.lead.remindIn')}
      </span>
      <div className="crm-snooze-options">
        {options.map((option) => (
          <button
            key={option.key}
            type="button"
            className="crm-snooze-btn"
            data-option={option.key}
            onClick={() => onPick(option.at())}
          >
            {t(`crm.lead.snooze.${option.key}`)}
          </button>
        ))}
        {value && !comeBack && (
          <button
            type="button"
            className="crm-snooze-btn crm-snooze-clear"
            onClick={() => onPick(null)}
          >
            <X size={13} /> {t('crm.lead.snooze.clear')}
          </button>
        )}
      </div>
      {withCalendar && <ComeBackDate onPick={onPick} />}
    </div>
  );
}
